import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Palette, Save, Loader2 } from "lucide-react";

interface EmailTemplateSettings {
  companyName: string;
  logoUrl: string;
  primaryColor: string;
  footerText: string;
  includeUnsubscribeLink: boolean;
  customCss: string;
}

const DEFAULT_SETTINGS: EmailTemplateSettings = {
  companyName: "TPS Housing Platform",
  logoUrl: "",
  primaryColor: "#3b82f6",
  footerText: "This is an automated report from TPS Housing Platform. To modify or unsubscribe from this report, please visit the Admin Reports section.",
  includeUnsubscribeLink: true,
  customCss: "",
};

export function EmailTemplateSettings() {
  const [settings, setSettings] = useState<EmailTemplateSettings>(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");

  useEffect(() => {
    // Load settings from localStorage (in a real app, this would be from the database)
    const savedSettings = localStorage.getItem("emailTemplateSettings");
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
    generatePreview();
  }, []);

  useEffect(() => {
    generatePreview();
  }, [settings]);

  const generatePreview = () => {
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px; 
            max-width: 800px; 
            margin: 0 auto;
            background-color: #f5f5f5;
          }
          .email-container {
            background-color: white;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          .header {
            background-color: ${settings.primaryColor};
            color: white;
            padding: 20px;
            text-align: center;
          }
          .header img {
            max-height: 50px;
            margin-bottom: 10px;
          }
          .content {
            padding: 30px;
          }
          h1 { 
            color: ${settings.primaryColor}; 
            border-bottom: 2px solid ${settings.primaryColor}; 
            padding-bottom: 10px;
            margin-top: 0;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 20px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 10px; 
            text-align: left; 
          }
          th { 
            background-color: ${settings.primaryColor}15; 
            color: ${settings.primaryColor};
          }
          .footer {
            background-color: #f8f9fa;
            padding: 20px;
            text-align: center;
            font-size: 12px;
            color: #666;
            border-top: 1px solid #eee;
          }
          .footer a {
            color: ${settings.primaryColor};
          }
          ${settings.customCss}
        </style>
      </head>
      <body>
        <div class="email-container">
          <div class="header">
            ${settings.logoUrl ? `<img src="${settings.logoUrl}" alt="Logo" />` : ""}
            <h2 style="margin: 0;">${settings.companyName}</h2>
          </div>
          <div class="content">
            <h1>Sample Report Name</h1>
            <p style="color: #666; font-size: 14px;">
              Scheduled Report - Generated on: ${format(new Date(), "PPpp")}
            </p>
            
            <h2 style="color: #333; margin-top: 30px;">SAMPLE DATA</h2>
            <table>
              <tr>
                <th>Column 1</th>
                <th>Column 2</th>
                <th>Column 3</th>
              </tr>
              <tr>
                <td>Sample Value 1</td>
                <td>Sample Value 2</td>
                <td>Sample Value 3</td>
              </tr>
              <tr>
                <td>Sample Value 4</td>
                <td>Sample Value 5</td>
                <td>Sample Value 6</td>
              </tr>
            </table>
          </div>
          <div class="footer">
            <p>${settings.footerText}</p>
            ${settings.includeUnsubscribeLink ? '<p><a href="#">Manage subscription preferences</a></p>' : ""}
          </div>
        </div>
      </body>
      </html>
    `;
    setPreviewHtml(html);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save to localStorage (in production, save to database)
      localStorage.setItem("emailTemplateSettings", JSON.stringify(settings));
      toast({ title: "Email template settings saved" });
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({ title: "Failed to save settings", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="w-5 h-5" />
          Email Template Customization
        </CardTitle>
        <CardDescription>
          Customize the branding and styling for scheduled report emails
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Settings Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={settings.companyName}
                onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                placeholder="Your Company Name"
              />
            </div>

            <div className="space-y-2">
              <Label>Logo URL (optional)</Label>
              <Input
                value={settings.logoUrl}
                onChange={(e) => setSettings({ ...settings, logoUrl: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
            </div>

            <div className="space-y-2">
              <Label>Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={settings.primaryColor}
                  onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                  placeholder="#3b82f6"
                  className="flex-1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Footer Text</Label>
              <Textarea
                value={settings.footerText}
                onChange={(e) => setSettings({ ...settings, footerText: e.target.value })}
                rows={3}
                placeholder="Footer message for email reports..."
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={settings.includeUnsubscribeLink}
                onCheckedChange={(checked) => setSettings({ ...settings, includeUnsubscribeLink: checked })}
              />
              <Label>Include unsubscribe/manage link</Label>
            </div>

            <div className="space-y-2">
              <Label>Custom CSS (optional)</Label>
              <Textarea
                value={settings.customCss}
                onChange={(e) => setSettings({ ...settings, customCss: e.target.value })}
                rows={4}
                placeholder=".custom-class { ... }"
                className="font-mono text-sm"
              />
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Template Settings
            </Button>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <Label>Email Preview</Label>
            <div className="border rounded-lg overflow-hidden h-[500px]">
              <iframe
                srcDoc={previewHtml}
                className="w-full h-full bg-white"
                title="Email Preview"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}